import React, { Fragment, useCallback, useMemo } from 'react';
import { Row } from '@thd-olt-component-react/core-ui';
import {
  Dropdown,
  MenuItem,
  Typography,
} from '@one-thd/sui-atomic-components';
import _isEmpty from 'lodash/isEmpty';
import { arrayOf, string } from 'prop-types';
import { useCabinetVisualizerStore } from '../../../../../store';
import { hasProductKeyFeatureByName } from '../../../../../store/collection/selectors';
import { generateAttributeTree } from '../../../../../utils/ssku-differentiation';
import logNewRelicCustom from '../../../../../utils/logNewRelicCustom';
import { NEW_RELIC_EVENTS } from '../../../../../constants';

export const getSelectedChoice = (currentAttribute, selectedSKU) => {
  if (!selectedSKU) {
    return currentAttribute.values[0];
  }

  if (currentAttribute.name.toLowerCase() === 'category') {
    const skuCategory = selectedSKU.categories[selectedSKU.categories.length - 1];
    return currentAttribute.values.find((value) => value.value === skuCategory) ?? currentAttribute.values[0];
  }
  const skuAttributeValue = hasProductKeyFeatureByName(selectedSKU, currentAttribute.name)?.value ?? 'No Value';

  return currentAttribute.values.find((value) => value.value === skuAttributeValue) ?? currentAttribute.values[0];
};

export const ProductTypeSelect = ({ modalTypes }) => {
  const { updateCabinetVisualizer } = useCabinetVisualizerStore((state) => state);
  const {
    styleChoiceId,
    skuDrawer: {
      attributePriority,
      modalAssortment,
      superSkuProducts,
      selectedParentId,
      selectedItemId
    }
  } = useCabinetVisualizerStore((state) => state.cabinetVisualizer);

  // Find SKUs and generate an attribute tree.
  // Doing this here allows multiple modal types with simplifies modal type definitions
  // Memo-ize as generateAttributeTree could be expensive.
  const modalSKUs = useMemo(() => modalTypes.flatMap((modalType) => (
    modalAssortment[modalType]?.skus || []
  )), [modalTypes]);
  if (modalTypes && (!modalSKUs || modalSKUs.length === 0)) {
    logNewRelicCustom(NEW_RELIC_EVENTS.NO_PRODUCTS_IN_MODAL,
      {
        superSkuProducts, selectedParentId, selectedItemId, styleChoiceId, modalTypes
      });
  }
  const currentTypeAttributeSet = useMemo(() => (
    generateAttributeTree(modalSKUs, attributePriority)
  ), [modalSKUs, attributePriority]);
  const currentSSKU = modalSKUs.find((sku) => sku.parentId === selectedParentId);

  let currentAttribute = currentTypeAttributeSet;
  let currentAttributeSelection = getSelectedChoice(currentAttribute, currentSSKU);
  const flattenedAttributeSet = [{
    name: currentAttribute.name,
    values: currentAttribute.values,
    selectedValue: currentAttributeSelection.value,
    selectedSku: currentAttributeSelection.sku
  }];
  while (currentAttributeSelection.children) {
    currentAttribute = currentAttributeSelection.children;
    currentAttributeSelection = getSelectedChoice(currentAttribute, currentSSKU);
    flattenedAttributeSet.push({
      ...currentAttribute, // This retains each sub-tree which isn't required but is nice on change
      selectedValue: currentAttributeSelection.value,
      selectedSku: currentAttributeSelection.sku
    });
  }

  if (!selectedParentId || !currentSSKU) {
    const defaultSSKU = flattenedAttributeSet[flattenedAttributeSet.length - 1].selectedSku;
    updateCabinetVisualizer({
      skuDrawer: {
        selectedParentId: defaultSSKU.parentId,
        selectedItemId: defaultSSKU.itemId,
      }
    });
  }

  const changeAttribute = useCallback((name, newValue) => {
    const changedAttribute = flattenedAttributeSet.find((attribute) => attribute.name === name);
    let attributeValue = changedAttribute.values.find((value) => value.value === newValue);

    // Walk down the tree of "first" selections to make sure every action selects a SSKU
    while (attributeValue.children) {
      attributeValue = getSelectedChoice(attributeValue.children);
    }

    updateCabinetVisualizer({
      skuDrawer: {
        selectedParentId: attributeValue.sku.parentId,
        selectedItemId: attributeValue.sku.itemId,
      }
    });
  });

  const showDropDowns = flattenedAttributeSet > 1 || flattenedAttributeSet[0]?.values?.length > 1;

  return (
    <Row>
      <div className="sui-pt-2 sui-w-full">
        {showDropDowns && flattenedAttributeSet.map((attribute) => (
          <Fragment key={attribute.name}>
            <Typography variant="body-small" color="primary">
              {attribute.name}: <b>{attribute.selectedValue}</b>
            </Typography>
            <div className="sui-pt-1">
              <Dropdown
                maxItems={5}
                value={attribute.selectedValue}
                onChange={(event) => changeAttribute(attribute.name, event.target.value)}
              >
                {attribute.values.map((value) => (
                  <MenuItem key={`${value.value}-key`} value={value.value}>{value.value}</MenuItem>
                ))}
              </Dropdown>
            </div>
          </Fragment>
        ))}
      </div>
    </Row>
  );
};

ProductTypeSelect.propTypes = {
  modalTypes: arrayOf(string)
};

ProductTypeSelect.defaultProps = {
  modalTypes: ['Base']
};
